import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const DealCard = ({ deal, onViewDetails, onUpdateStage }) => {
  const getStageColor = (stage) => {
    switch (stage) {
      case 'inquiry': return 'bg-muted text-muted-foreground';
      case 'initial_contact': return 'bg-primary/10 text-primary';
      case 'negotiation': return 'bg-accent/10 text-accent';
      case 'due_diligence': return 'bg-secondary/10 text-secondary';
      case 'contract_finalization': return 'bg-success/10 text-success';
      case 'completed': return 'bg-success text-success-foreground';
      case 'cancelled': return 'bg-destructive/10 text-destructive';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-destructive';
      case 'normal': return 'text-accent';
      case 'low': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return 'No deadline';
    const date = new Date(deadline);
    const daysLeft = Math.ceil((date - new Date()) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return 'Overdue';
    if (daysLeft === 0) return 'Today';
    if (daysLeft === 1) return 'Tomorrow';
    return `${daysLeft} days`;
  };

  const completedMilestones = deal?.milestones?.filter(m => m?.completed)?.length || 0;
  const totalMilestones = deal?.milestones?.length || 0;
  const progress = totalMilestones > 0 ? (completedMilestones / totalMilestones) * 100 : 0;

  return (
    <div className="bg-card border border-border rounded-lg p-4 hover:shadow-lg transition-all duration-300">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-1 rounded text-xs font-medium ${getStageColor(deal?.currentStage)}`}>
              {deal?.currentStage?.replace('_', ' ')?.toUpperCase()}
            </span>
            {deal?.isUrgent && (
              <span className="flex items-center gap-1 text-xs text-destructive">
                <Icon name="AlertCircle" size={12} />
                Urgent
              </span>
            )}
          </div>
          <h3 className="text-base font-bold text-foreground mb-1 line-clamp-1">{deal?.dealTitle}</h3>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{deal?.player?.fullName}</span>
            <span>•</span>
            <span>{deal?.player?.primaryPosition}</span>
            <span>•</span>
            <span className="font-medium text-success">€{deal?.proposedFee}M</span>
          </div>
        </div>
        {deal?.player?.profilePhoto && (
          <Image
            src={deal?.player?.profilePhoto}
            alt={`${deal?.player?.fullName} profile photo`}
            className="w-12 h-12 rounded-full object-cover"
          />
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span>Progress</span>
          <span>{completedMilestones}/{totalMilestones} milestones</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className="bg-primary rounded-full h-2 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Next Action */}
      {deal?.nextAction && (
        <div className="bg-muted/50 rounded-lg p-2 mb-3">
          <div className="flex items-start gap-2">
            <Icon name="CheckCircle" size={14} className="mt-0.5 text-primary" />
            <div className="flex-1">
              <div className="text-xs font-medium text-foreground">{deal?.nextAction}</div>
              {deal?.nextDeadline && (
                <div className="text-xs text-muted-foreground mt-1">
                  Due: {formatDeadline(deal?.nextDeadline)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Icon name="Building" size={12} />
            <span>{deal?.club?.organization || deal?.club?.full_name}</span>
          </div>
          <div className={`flex items-center gap-1 ${getPriorityColor(deal?.priorityLevel)}`}>
            <Icon name="Flag" size={12} />
            <span>{deal?.priorityLevel}</span>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => onViewDetails(deal)}>
          View Details
        </Button>
      </div>
    </div>
  );
};

export default DealCard;